import { Injectable } from '@angular/core';
import { HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ClearingTicketCustomService,
  ClearingTicketService,
  SeverityType,
  StatusType,
} from '@adlatus-gui/domain/clearing';
import {
  ClearingTicketAssignmentService,
  ClearingTicketBase,
  ClearingTicketWatchService,
} from '@adlatus-gui/domain/clearing-custom';
import { convertToIsoString } from '@adlatus-gui/shared';

@Injectable({
  providedIn: 'root',
})
export class ClearingTicketUtils {
  public static readonly _xTotalCount = 'X-Total-Count';

  private _totalInbox = new BehaviorSubject<number>(0);
  private _totalOutbox = new BehaviorSubject<number>(0);
  private _totalMyIssues = new BehaviorSubject<string>('0');
  private _totalWatchingIssues = new BehaviorSubject<string>('0');

  get totalInbox$(): Observable<number> {
    return this._totalInbox.asObservable();
  }

  get totalOutbox$(): Observable<number> {
    return this._totalOutbox.asObservable();
  }

  get totalMyIssues$(): Observable<string> {
    return this._totalMyIssues.asObservable();
  }

  get totalWatchingIssues$(): Observable<string> {
    return this._totalWatchingIssues.asObservable();
  }

  updateInboxCount(count: number): void {
    this._totalInbox.next(count);
  }

  updateOutboxCount(count: number): void {
    this._totalOutbox.next(count);
  }

  updateMyIssuesCount(count: string): void {
    this._totalMyIssues.next(count);
  }

  updateWatchingIssues(count: string): void {
    this._totalWatchingIssues.next(count);
  }

  constructor(
    private clearingTicketService: ClearingTicketService,
    private clearingTicketAssignmentService: ClearingTicketAssignmentService,
    private clearingTicketCustomService: ClearingTicketCustomService,
    private watchClearingTicketService: ClearingTicketWatchService
  ) {}

  listClearingTicketByProcessor(
    searchQuery: string,
    processor: string|undefined,
    externalId: string | undefined,
    ticketType: string | undefined,
    originator: string | undefined,
    severity: SeverityType | undefined,
    requestedResolutionFrom:Date | undefined,
    requestedResolutionTo:Date | undefined,
    creationDateFrom: Date | undefined,
    creationDateTo: Date | undefined,
    status: StatusType | undefined,
    unread: boolean | undefined,
    slaWarning: boolean | undefined,
    slaExceeded: boolean | undefined,
    notSynced: boolean | undefined,
    proxyWaitingTargetResolution: boolean | undefined,
    allTickets: boolean | undefined,
    finalizedOrClosedOnly: boolean | undefined,
    lastUpdateFrom: Date | undefined,
    lastUpdateTo: Date | undefined,
    offset: number,
    limit: number
  ): Observable<HttpResponse<Array<ClearingTicketBase>>> {
    return this.clearingTicketCustomService.listClearingTicket(
      searchQuery, // searchQuery
      externalId, // externalId
      ticketType, // ticketType
      status, // status
      originator, // originator
      processor, // processor
      severity, // severity
      requestedResolutionFrom ? convertToIsoString(requestedResolutionFrom) : undefined, // requestedResolutionDateFrom
      requestedResolutionTo ? convertToIsoString(requestedResolutionTo) : undefined, // requestedResolutionDateTo
      creationDateFrom ? convertToIsoString(creationDateFrom) : undefined, // creationDateFrom
      creationDateTo ? convertToIsoString(creationDateTo) : undefined,// creationDateTo
      lastUpdateFrom, // lastUpdateFrom
      lastUpdateTo, // lastUpdateTo
      unread, // unread custom filter
      slaWarning, //sla-warning custom filter
      slaExceeded, //sla-exceeded custom filter
      notSynced, // notSynced custom filter
      proxyWaitingTargetResolution, // proxy waiting target resolution
      undefined, //myIssues custom filter
      undefined,
      true,
      undefined,
      allTickets,
      finalizedOrClosedOnly,
      offset, // offset
      limit, // limit
      'response', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  listClearingTicketByOriginator(
    searchQuery: string,
    originator: string |undefined,
    ticketType: string | undefined,
    processor: string | undefined,
    externalId: string | undefined,
    severity: SeverityType | undefined,
    requestedResolutionFrom:Date | undefined,
    requestedResolutionTo:Date | undefined,
    creationDateFrom: Date | undefined,
    creationDateTo: Date | undefined,
    status: StatusType | undefined,
    unread: boolean | undefined,
    slaWarning: boolean | undefined,
    slaExceeded: boolean | undefined,
    notSynced: boolean | undefined,
    proxyWaitingTargetResolution: boolean | undefined,
    allTickets: boolean | undefined,
    finalizedOrClosedOnly: boolean | undefined,
    lastUpdateFrom: Date | undefined,
    lastUpdateTo: Date | undefined,
    offset: number,
    limit: number
  ): Observable<HttpResponse<Array<ClearingTicketBase>>> {
    return this.clearingTicketCustomService.listClearingTicket(
      searchQuery, // searchQuery
      externalId, // externalId
      ticketType, // ticketType
      status, // status
      originator, // originator
      processor, // processor
      severity, // severity
      requestedResolutionFrom ? convertToIsoString(requestedResolutionFrom) : undefined, // requestedResolutionDateFrom
      requestedResolutionTo ? convertToIsoString(requestedResolutionTo) : undefined, // requestedResolutionDateTo
      creationDateFrom ? convertToIsoString(creationDateFrom) : undefined, // creationDateFrom
      creationDateTo ? convertToIsoString(creationDateTo) : undefined,// creationDateTo
      lastUpdateFrom, // lastUpdateFrom
      lastUpdateTo, // lastUpdateTo
      unread, // unread custom filter
      slaWarning, //sla-warning custom filter
      slaExceeded, //sla-exceeded custom filter
      notSynced, // notSynced custom filter
      proxyWaitingTargetResolution, // proxy waiting target resolution
      undefined, //myIssues custom filter
      undefined,
      undefined,
      true, //outbox custom filter
      allTickets,
      finalizedOrClosedOnly,
      offset, // offset
      limit, // limit
      'response', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  listClearingTickets(
    searchQuery: string,
    originator: string | undefined,
    processor: string | undefined,
    externalId: string | undefined,
    ticketType: string | undefined,
    severity: SeverityType | undefined,
    requestedResolutionFrom:Date | undefined,
    requestedResolutionTo:Date | undefined,
    creationDateFrom: Date | undefined,
    creationDateTo: Date | undefined,
    status: StatusType | undefined,
    unread: boolean | undefined,
    slaWarning: boolean | undefined,
    slaExceeded: boolean | undefined,
    notSynced: boolean | undefined,
    proxyWaitingTargetResolution: boolean | undefined,
    myIssues: boolean | undefined,
    myWatchingIssues: boolean | undefined,
    allTickets: boolean | undefined,
    finalizedOrClosedOnly: boolean | undefined,
    lastUpdateFrom: Date | undefined,
    lastUpdateTo: Date | undefined,
    offset: number,
    limit: number
  ): Observable<HttpResponse<Array<ClearingTicketBase>>> {
    return this.clearingTicketCustomService.listClearingTicket(
      searchQuery, // searchQuery
      externalId, // externalId
      ticketType, // ticketType
      status, // status
      originator, // originator
      processor, // processor
      severity, // severity
      requestedResolutionFrom ? convertToIsoString(requestedResolutionFrom) : undefined, // requestedResolutionDateFrom
      requestedResolutionTo ? convertToIsoString(requestedResolutionTo) : undefined, // requestedResolutionDateTo
      creationDateFrom ? convertToIsoString(creationDateFrom) : undefined, // creationDateFrom
      creationDateTo ? convertToIsoString(creationDateTo) : undefined,// creationDateTo
      lastUpdateFrom, // lastUpdateFrom
      lastUpdateTo, // lastUpdateTo
      unread, // unread custom filter
      slaWarning, //sla-warning custom filter
      slaExceeded, //sla-exceeded custom filter
      notSynced, // notSynced custom filter
      proxyWaitingTargetResolution, // proxy waiting target resolution
      myIssues, //myIssuesCustomFilter
      myWatchingIssues, //myWatchingIssuesCustomFilter
      undefined,
      undefined,
      allTickets,
      finalizedOrClosedOnly,
      offset, // offset
      limit, // limit
      'response', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  myTickets(
    searchQuery?: string,
    ticketType?: string,
    status?: StatusType,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    creationDateFrom?: Date,
    creationDateTo?: Date,
    unread?: boolean,
    slaWarning?: boolean,
    slaExceeded?: boolean,
    notSynced?: boolean,
    page?: number,
    size?: number
  ): Observable<Array<ClearingTicketBase>> {
    return this.clearingTicketCustomService.listClearingTicket(
      searchQuery, // searchQuery
      undefined, // externalId
      ticketType, // ticketType
      status, // status
      originator, // originator
      processor, // processor
      severity, // severity
      undefined, // requestedResolutionDateFrom
      undefined, // requestedResolutionDateTo
      creationDateFrom ? convertToIsoString(creationDateFrom) : undefined, // creationDateFrom
      creationDateTo ? convertToIsoString(creationDateTo) : undefined, // creationDateTo
      undefined, // lastUpdateFrom
      undefined, // lastUpdateTo
      unread, // unread custom filter
      slaWarning, //sla-warning custom filter
      slaExceeded, //sla-exceeded custom filter
      notSynced, // notSynced custom filter
      undefined,
      true, //myIssuesCustomFilter
      undefined, //myWatchingIssuesCustomFilter
      undefined,
      undefined,
      undefined, // allTickets
      undefined, // finalizedOrClosedOnly
      undefined, // offset
      undefined, // limit
      'body', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  myWatchingTickets(
    searchQuery?: string,
    ticketType?: string,
    status?: StatusType,
    originator?: string,
    processor?: string,
    severity?: SeverityType,
    creationDateFrom?: Date,
    creationDateTo?: Date,
    unread?: boolean,
    slaWarning?: boolean,
    slaExceeded?: boolean,
    notSynced?: boolean,
    page?: number,
    size?: number
  ): Observable<Array<ClearingTicketBase>> {
    return this.clearingTicketCustomService.listClearingTicket(
      searchQuery, // searchQuery
      undefined, // externalId
      ticketType, // ticketType
      status, // status
      originator, // originator
      processor, // processor
      severity, // severity
      undefined, // requestedResolutionDateFrom
      undefined, // requestedResolutionDateTo
      creationDateFrom ? convertToIsoString(creationDateFrom) : undefined, // creationDateFrom
      creationDateTo ? convertToIsoString(creationDateTo) : undefined, // creationDateTo
      undefined, // lastUpdateFrom
      undefined, // lastUpdateTo
      unread, // unread custom filter
      slaWarning, //sla-warning custom filter
      slaExceeded, //sla-exceeded custom filter
      notSynced, // notSynced custom filter
      undefined,
      undefined, //myIssuesCustomFilter
      true, //myWatchingIssuesCustomFilter
      undefined,
      undefined,
      undefined,
      undefined,
      undefined, // offset
      undefined, // limit
      'body', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  countClearingTickets(
    processor: string | undefined,
    originator: string | undefined,
    currentStatus: string | undefined,
    severity: SeverityType | undefined,
    creationDateFrom: string | undefined,
    lastStatusChangeFrom: string | undefined
  ): Observable<HttpResponse<number>> {
    return this.clearingTicketCustomService.countClearingTicket(
      undefined, // externalId
      undefined, // ticketType
      currentStatus, // status
      originator, // originator
      processor, // processor
      severity, // severity
      undefined, // requestedResolutionDateFrom
      undefined, // requestedResolutionDateTo
      creationDateFrom, // creationDateFrom
      undefined, // creationDateTo
      undefined, // lastUpdateFrom
      undefined, // lastUpdateTo
      lastStatusChangeFrom, // lastStatusChangeFrom
      false, //myWatchingIssues
      false, //myIssues
      'response', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  countMyWatchingTickets() {
    return this.clearingTicketCustomService.countClearingTicket(
      undefined, // externalId
      undefined, // ticketType
      undefined, // status
      undefined, // originator
      undefined, // processor
      undefined, // severity
      undefined, // requestedResolutionDateFrom
      undefined, // requestedResolutionDateTo
      undefined, // creationDateFrom
      undefined, // creationDateTo
      undefined, // lastUpdateFrom
      undefined, // lastUpdateTo
      undefined, // lastStatusChangeFrom
      true, // myWatchingIssues
      false, // myIssues
      'response', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  countMyTickets() {
    return this.clearingTicketCustomService.countClearingTicket(
      undefined, // externalId
      undefined, // ticketType
      undefined, // status
      undefined, // originator
      undefined, // processor
      undefined, // severity
      undefined, // requestedResolutionDateFrom
      undefined, // requestedResolutionDateTo
      undefined, // creationDateFrom
      undefined, // creationDateTo
      undefined, // lastUpdateFrom
      undefined, // lastUpdateTo
      undefined, // lastStatusChangeFrom
      false, // myWatchingIssues
      true, // myIssues
      'response', // observe (optional, set to 'body' if needed)
      false // reportProgress (optional, set to false if needed)
    );
  }

  getLastSunday(): string {
    const today = new Date();
    const lastSunday = new Date(
      today.setDate(today.getDate() - today.getDay())
    );
    return lastSunday.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }
}
